/**
 * Determines which cell component is rendered for a column.
 *
 * | Value        | Component    | Description                              |
 * |--------------|--------------|------------------------------------------|
 * | `text-cell`  | TextCell     | Read-only text, optional icon(s)         |
 * | `input-cell` | InputCell    | Editable text; user can lock column read-only in the column panel |
 * | `select-cell`| SelectCell   | Editable dropdown from a fixed option list|
 * | `array-cell` | ArrayCell    | Renders a list of string chips/tags      |
 * | `tooltip-cell`| TooltipCell | Truncated text with a hover tooltip      |
 * | `actions-cell`| —           | No primary cell value; use `headerActions` / `rowActions` for tooling |
 */
export const FieldType = {
  TextCell: "text-cell",
  InputCell: "input-cell",
  SelectCell: "select-cell",
  ArrayCell: "array-cell",
  TooltipCell: "tooltip-cell",
  ActionsCell: "actions-cell",
} as const;

export type FieldType = (typeof FieldType)[keyof typeof FieldType];

/** A fixed option shown in a SelectCell dropdown. */
export type SelectColumnOption = { value: string | number; label: string };

/**
 * Controls where an icon is rendered relative to the cell text.
 *
 * - `Prepend` — icon placed before the text
 * - `Append`  — icon placed after the text
 * - `Main`    — icon replaces the text entirely
 */
export const ColumnIconType = {
  Prepend: "prepend",
  Append: "append",
  Main: "main",
} as const;

export type ColumnIconType = (typeof ColumnIconType)[keyof typeof ColumnIconType];

/** The resolved icon descriptor passed to the icon component. */
export type ColumnIconValue = {
  /** Icon class/code, e.g. `"n-i-football"`. */
  code: string;
  /** Optional CSS color value, e.g. `"rgb(76, 175, 80)"` or a CSS variable. */
  color?: string;
};

/**
 * Icon configuration attached to a TextColumn.
 * The `formatter` is called with the full row and must return a `ColumnIconValue`.
 *
 * @example
 * icon: {
 *   type: ColumnIconType.Prepend,
 *   formatter: (row) => {
 *     const item = row as { sportCode: string };
 *     return { code: `n-i-${item.sportCode}` };
 *   },
 * }
 */
export type ColumnIcon = {
  type: ColumnIconType;
  formatter: (row: unknown) => ColumnIconValue;
};

/** Passed to optional **header** actions on a column. No row — header chrome only. */
export type StickyHeaderActionContext = {
  /** Re-run the grid list GET. */
  refetch: () => Promise<void>;
  /**
   * The mounted grid block (`dialogs`, `api`, filters, …).
   */
  grid: import("../view/types").GridViewBlock;
  /** Opens the column visibility / reorder floating panel (wired to `ColumnsControlPanel` when the column has `columnsPanelTitle`). */
  openColumnsPanel: () => void;
};

/** Passed to optional **row** actions on a column. */
export type StickyRowActionContext = {
  /** Row the user acted on. */
  row: Record<string, unknown>;
  /** Opens `grid.dialogs[dialogKey]` for this row (dialog host). */
  openDialog: (dialogKey: string) => void;
  /** Re-run the grid list GET. */
  refetch: () => Promise<void>;
  /** Local or remote row delete (see `useGridMutations`). */
  deleteRow: (id: unknown) => Promise<void>;
  /**
   * The mounted grid block (`dialogs`, `api`, filters, …). Lets `onClick` branch on config without closures.
   */
  grid: import("../view/types").GridViewBlock;
};

/** Optional toolbar action in the **table header** for a column (e.g. open column panel). */
export type StickyHeaderChromeAction = {
  /** Design-system icon code (e.g. `"table-d"`). */
  icon: string;
  /** i18n key or label for accessibility (passed through `t()` in the grid). */
  label: string;
  /**
   * Click handler. If omitted and the column has `columnsPanelTitle`, defaults to `(ctx) => ctx.openColumnsPanel()`.
   */
  onClick?: (ctx: StickyHeaderActionContext) => void | Promise<void>;
};

/** Optional action buttons on **each data row** for a column. */
export type StickyRowChromeAction = {
  icon: string;
  label: string;
  onClick: (ctx: StickyRowActionContext) => void | Promise<void>;
};

/**
 * Fields shared by every column variant.
 *
 * ### Update payload resolution
 * When an editable cell commits a change, the update payload is resolved in this priority order:
 *
 * 1. **`payloadOutFormatter`** — full control; receives `(value, row)` and returns the payload object.
 * 2. **`apiPath`** — dot-notation path that is expanded into a nested object,
 *    e.g. `"prematchGame.position"` + `5` → `{ prematchGame: { position: 5 } }`.
 * 3. **`key`** — fallback; sends `{ [key]: value }` directly.
 */
type BaseColumn = {
  /** Unique identifier for the column. Used as the fallback key in update payloads. */
  key: string;
  /** Whether the column is visible before the user customises the column set. */
  defaultVisibility: boolean;
  /**
   * When `false`, the column cannot be hidden from the column panel and always stays in the grid.
   * Use for chrome / action columns the user must not turn off (avoids “lost” panel access).
   * @default true
   */
  configurableVisibility?: boolean;
  /** i18n key for the column header label. */
  title: string;
  /** CSS grid track size, e.g. `"120px"` or `"2fr"`. Defaults to `"1fr"`. When set, auto-sizing is skipped for this column. */
  width?: string;
  /**
   * Maximum width in pixels that auto-sizing can assign to this column.
   * Falls back to `DEFAULT_MAX_COLUMN_WIDTH` (400px) when absent.
   * Has no effect when `width` is explicitly set.
   */
  maxWidth?: number;
  /** Whether the column header is clickable for server-side sorting. */
  sort?: {
    enabled: boolean;
    key: string;
  };
  /**
   * Dot-notation path used to build a nested update payload.
   * Takes precedence over `key` but is overridden by `payloadOutFormatter`.
   *
   * @example
   * apiPath: "prematchGame.position"
   * // Sends: { prematchGame: { position: <value> } }
   */
  apiPath?: string;
  /**
   * Fully custom update payload builder. Takes precedence over both `apiPath` and `key`.
   * Use this when you need to merge the edited value with other row fields
   * or restructure the payload beyond a simple dot-path.
   *
   * @param value - The raw value emitted by the cell component.
   * @param row   - The full row data at the time of the edit.
   * @returns     The complete payload object passed to the API update call.
   *
   * @example
   * payloadOutFormatter: (value, row) => ({
   *   prematchGame: {
   *     ...(row.prematchGame as object),
   *     position: Number(value),
   *   },
   * })
   */
  payloadOutFormatter?: (value: unknown, row: Record<string, unknown>) => Record<string, unknown>;
  /** When true, column participates in horizontal sticky positioning in the grid. */
  sticky?: boolean;
  /** Header toolbar buttons for this column (e.g. column panel trigger). */
  headerActions?: StickyHeaderChromeAction[];
  /** Per-row trailing buttons (e.g. edit/delete). */
  rowActions?: StickyRowChromeAction[];
  /**
   * When set, mounts `ColumnsControlPanel` for this column (opened via header action context `openColumnsPanel`).
   */
  columnsPanelTitle?: string;
  /** When it returns false, editable cells are read-only for that row. */
  rowEditable?: (row: Record<string, unknown>) => boolean;
};

/**
 * Read-only text column. Supports an optional icon rendered before, after, or
 * instead of the text via `ColumnIconType`.
 *
 * Use `valueFormatter` to derive the displayed string from the full row when the
 * value is not a flat `row[key]` property (e.g. nested fields or translated enums).
 *
 * @example
 * {
 *   key: "sportName",
 *   fieldType: FieldType.TextCell,
 *   defaultVisibility: true,
 *   title: "table_columns.sport",
 *   icon: {
 *     type: ColumnIconType.Prepend,
 *     formatter: (row) => ({ code: `n-i-${(row as any).sportCode}` }),
 *   },
 * }
 */
export type TextColumn = BaseColumn & {
  fieldType: typeof FieldType.TextCell;
  /** Derive the display string from the full row. When absent, `row[key]` is used. */
  valueFormatter?: (row: unknown) => string;
  icon?: ColumnIcon;
};

/**
 * Inline editable text input column. Commits on Enter key.
 *
 * Supply `apiPath` or `payloadOutFormatter` when the API expects a nested
 * structure rather than a flat `{ [key]: value }` payload.
 *
 * @example
 * {
 *   key: "prematchGamePosition",
 *   fieldType: FieldType.InputCell,
 *   defaultVisibility: true,
 *   title: "table_columns.prematch_position",
 *   apiPath: "prematchGame.position",
 *   valueFormatter: (row) => (row as any).prematchGame.position.toString(),
 * }
 *
 * Input columns are user-lockable by default (column panel + read-only when locked, stored in
 * localStorage). Set `lockable: false` to disable locking where it does not fit.
 */
export type InputColumn = BaseColumn & {
  fieldType: typeof FieldType.InputCell;
  /** Derive the display string from the full row. When absent, `row[key]` is used. */
  valueFormatter?: (row: unknown) => string;
  /**
   * When `false`, the user cannot lock this column read-only (no lock control, edits always allowed).
   * When omitted or `true`, locking is allowed when the grid defines a column with `columnsPanelTitle`
   * (column visibility panel).
   */
  lockable?: boolean;
};

/**
 * Read-only column that renders a list of string values as chips or tags.
 *
 * `valueFormatter` must return a `string[]`. When absent, `row[key]` is
 * expected to already be a `string[]`.
 *
 * @example
 * {
 *   key: "feeds",
 *   fieldType: FieldType.ArrayCell,
 *   defaultVisibility: true,
 *   title: "table_columns.feeds",
 *   valueFormatter: (row) => (row as any).feeds.map((f: any) => f.name),
 * }
 */
export type ArrayColumn = BaseColumn & {
  fieldType: typeof FieldType.ArrayCell;
  /** Derive the string array from the full row. When absent, `row[key]` is used. */
  valueFormatter?: (row: unknown) => string[];
};

/**
 * Editable dropdown column. The user picks from a fixed list of `options`.
 *
 * `valueFormatter` controls the display string shown when the cell is not in
 * edit mode (e.g. translating a numeric status code to a label).
 */
export type SelectColumn = BaseColumn & {
  fieldType: typeof FieldType.SelectCell;
  options: SelectColumnOption[];
  /** Derive the display string from the full row. When absent, `row[key]` is used. */
  valueFormatter?: (row: unknown) => string;
};

/**
 * Read-only column that shows a truncated value with the full text in a hover tooltip.
 *
 * Use `valueFormatter` when the tooltip content must be derived from the row
 * rather than read directly from `row[key]`.
 */
export type TooltipColumn = BaseColumn & {
  fieldType: typeof FieldType.TooltipCell;
  /** Derive the display string from the full row. When absent, `row[key]` is used. */
  valueFormatter?: (row: unknown) => string;
};

/**
 * Column with no primary data cell — only optional `headerActions` / `rowActions` (and `sticky`, etc.).
 * Use for trailing chrome columns; pair with `rowActions` for per-row buttons.
 */
export type ActionsColumn = BaseColumn & {
  fieldType: typeof FieldType.ActionsCell;
};

/** Discriminated union of all supported column variants. Narrow via `fieldType`. */
export type Column =
  | TextColumn
  | InputColumn
  | ArrayColumn
  | SelectColumn
  | TooltipColumn
  | ActionsColumn;

/**
 * Groups a set of related columns under a shared header label in the grid.
 * Columns are defined inline — the group owns its members directly.
 *
 * A group header is rendered as a spanning top row only when at least two of
 * its columns are both visible **and** consecutive in the current column order.
 * If members are hidden or reordered to be non-adjacent the group header is
 * suppressed automatically.
 *
 * @example
 * {
 *   type: "group",
 *   key: "prematch",
 *   title: "table_columns.prematch_group",
 *   columns: [
 *     { key: "prematchGameStatus", fieldType: FieldType.TextCell, ... },
 *     { key: "prematchGamePosition", fieldType: FieldType.InputCell, ... },
 *   ],
 * }
 */
export type ColumnGroupDef = {
  type: "group";
  /** Unique identifier for the group. */
  key: string;
  /** i18n key for the group header label. */
  title: string;
  /** Columns that belong to this group, defined inline. */
  columns: Column[];
};

/** A single entry in a grid column list — either a plain column or a group of columns. */
export type ColumnEntry = Column | ColumnGroupDef;
